import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bell, Printer, FileDown, PhoneCall, Users, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const TYPE_OPTS = [["call", "Call"], ["visit", "Visit"], ["whatsapp", "WhatsApp"], ["email", "Email"]];
const PRIORITY_OPTS = [["high", "High"], ["medium", "Medium"], ["low", "Low"]];
const STATUS_LABEL = { not_started: "Not Started", pending: "Pending", completed: "Completed" };

const emptyFu = { follow_up_date: "", follow_up_time: "", follow_up_type: "call",
                  assigned_to: "", priority: "medium", remarks: "", status: "pending" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function FollowUpDialog({ open, onClose, row, bulkCount, staff, onSaved }) {
  const [f, setF] = useState(emptyFu);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (row?.follow_up) {
      const fu = row.follow_up;
      setF({ follow_up_date: fu.follow_up_date || "", follow_up_time: fu.follow_up_time || "",
             follow_up_type: fu.follow_up_type || "call", assigned_to: fu.assigned_to || "",
             priority: fu.priority || "medium", remarks: fu.remarks || "", status: fu.status || "pending" });
    } else setF(emptyFu);
  }, [open, row]);

  const save = async () => {
    if (!f.follow_up_date) { toast.error("Follow Up Date is required"); return; }
    setBusy(true);
    try {
      if (bulkCount > 0) {
        const targets = row.map(r => ({ customer_id: r.customer_id, vehicle_id: r.vehicle_id }));
        const { data } = await api.post("/follow-ups/bulk", { targets, ...f });
        toast.success(`${data.total} follow-up reminder${data.total === 1 ? "" : "s"} created successfully (${data.created} new, ${data.updated} updated)`);
      } else {
        await api.post("/follow-ups", { customer_id: row.customer_id, vehicle_id: row.vehicle_id, ...f });
        toast.success("Follow-up reminder saved");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-lg" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">
            {bulkCount > 0 ? `Bulk Follow Up · ${bulkCount} customer${bulkCount === 1 ? "" : "s"}` : `Follow Up · ${row?.customer_name || ""}`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {bulkCount > 0 ? "The same follow up details will be created for all selected customers." : "Create or update the follow up reminder for this customer."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Follow Up Date *</Label>
              <Input data-testid="fu-date" type="date" value={f.follow_up_date} onChange={(e) => setF(x => ({ ...x, follow_up_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Follow Up Time</Label>
              <Input data-testid="fu-time" type="time" value={f.follow_up_time} onChange={(e) => setF(x => ({ ...x, follow_up_time: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Follow Up Type</Label>
              <Select value={f.follow_up_type} onValueChange={(v) => setF(x => ({ ...x, follow_up_type: v }))}>
                <SelectTrigger data-testid="fu-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{TYPE_OPTS.map(([v, l]) => <SelectItem key={v} data-testid={`fu-type-${v}`} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Assigned To</Label>
              <Select value={f.assigned_to} onValueChange={(v) => setF(x => ({ ...x, assigned_to: v }))}>
                <SelectTrigger data-testid="fu-assigned" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                <SelectContent>{staff.map(s => <SelectItem key={s.id} data-testid={`fu-assigned-${s.id}`} value={s.id}>{s.name} · {s.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF(x => ({ ...x, priority: v }))}>
                <SelectTrigger data-testid="fu-priority" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{PRIORITY_OPTS.map(([v, l]) => <SelectItem key={v} data-testid={`fu-priority-${v}`} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {bulkCount === 0 && (
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                <Select value={f.status} onValueChange={(v) => setF(x => ({ ...x, status: v }))}>
                  <SelectTrigger data-testid="fu-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem data-testid="fu-status-pending" value="pending">Pending</SelectItem>
                    <SelectItem data-testid="fu-status-completed" value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Remarks</Label>
            <Textarea data-testid="fu-remarks" value={f.remarks} onChange={(e) => setF(x => ({ ...x, remarks: e.target.value }))} rows={2} className="mt-1 rounded-sm bg-secondary border-border"/>
          </div>
          <Button data-testid="fu-save" onClick={save} disabled={busy} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
            {busy ? "Saving…" : bulkCount > 0 ? `Create ${bulkCount} Reminder${bulkCount === 1 ? "" : "s"}` : "Save Follow Up"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Reminders() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [checking, setChecking] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", status: "all", service: "all" });
  const [selected, setSelected] = useState({});
  const [dialog, setDialog] = useState({ open: false, row: null, bulk: false });

  const load = () => {
    api.get("/follow-ups/customers").then(r => setRows(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/follow-ups/staff").then(r => setStaff(r.data)).catch(() => {});
    api.get("/reminders").then(r => setAlerts((r.data || []).filter(x => x.status === "pending"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const dismissAlert = async (id) => {
    try {
      await api.post(`/reminders/dismiss/${id}`);
      setAlerts(a => a.filter(x => x.id !== id));
      toast.success("Alert dismissed");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/reminders/generate");
      toast.success(data.created ? `${data.created} new service due alert${data.created === 1 ? "" : "s"} created` : "No new service due alerts");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setChecking(false); }
  };

  const serviceTypes = useMemo(() => [...new Set(rows.map(r => r.job_type).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    if (filters.status !== "all" && r.status !== filters.status) return false;
    if (filters.service !== "all" && r.job_type !== filters.service) return false;
    const nd = (r.next_due_date || "").slice(0, 10);
    if (filters.from && (!nd || nd < filters.from)) return false;
    if (filters.to && (!nd || nd > filters.to)) return false;
    return true;
  }), [rows, filters]);
  const pg = usePagination(filtered, filters);

  const selectedRows = filtered.filter(r => selected[`${r.customer_id}:${r.vehicle_id}`]);
  const allChecked = filtered.length > 0 && selectedRows.length === filtered.length;
  const someChecked = selectedRows.length > 0 && !allChecked;

  const toggleAll = (v) => {
    const next = { ...selected };
    filtered.forEach(r => { next[`${r.customer_id}:${r.vehicle_id}`] = v === true; });
    setSelected(next);
  };
  const toggleOne = (r, v) => setSelected(s => ({ ...s, [`${r.customer_id}:${r.vehicle_id}`]: v === true }));

  const openSingle = (r) => setDialog({ open: true, row: r, bulk: false });
  const openBulk = () => setDialog({ open: true, row: selectedRows, bulk: true });
  const closeDialog = () => setDialog({ open: false, row: null, bulk: false });

  const exportCsv = () => {
    const head = ["Customer Name", "Mobile", "Vehicle Number", "Last Service Date", "Next Due Date", "Due Odometer", "Job Type", "Follow Up Status"];
    const lines = filtered.map(r => [r.customer_name, r.mobile, r.vehicle_number,
      fmtDate(r.last_service_date), fmtDate(r.next_due_date), r.next_service_odometer ? `${r.next_service_odometer} km` : "-", r.job_type || "-", STATUS_LABEL[r.status]]);
    const csv = [head, ...lines].map(cols => cols.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "follow_up_reminders.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const badge = (s) => s === "completed"
    ? <Badge className="rounded-sm text-[10px] uppercase tracking-wider">Completed</Badge>
    : s === "pending"
      ? <Badge variant="secondary" className="rounded-sm text-[10px] uppercase tracking-wider text-yellow-500">Pending</Badge>
      : <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider text-muted-foreground">Not Started</Badge>;

  return (
    <div className="p-8" data-testid="followup-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Bell className="w-3 h-3"/> CRM · Job card follow ups</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Follow Up Reminder</h1>
          <div className="text-sm text-muted-foreground mt-2">Customers due for their next service, based on job cards</div>
        </div>
        <div className="flex gap-2">
          {selectedRows.length > 0 && (
            <Button data-testid="bulk-followup-btn" onClick={openBulk} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
              <Users className="w-4 h-4 mr-1"/> Bulk Follow Up ({selectedRows.length})
            </Button>
          )}
          <Button data-testid="fu-print-btn" variant="outline" onClick={() => window.print()} className="rounded-sm uppercase tracking-[0.15em] text-xs">
            <Printer className="w-4 h-4 mr-1"/> Print
          </Button>
          <Button data-testid="fu-export-btn" variant="outline" onClick={exportCsv} className="rounded-sm uppercase tracking-[0.15em] text-xs">
            <FileDown className="w-4 h-4 mr-1"/> Export
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-4 mb-4" data-testid="service-alerts-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
              <Bell className="w-3 h-3 text-primary"/> Service Due Alerts
              <Badge variant="secondary" className="rounded-sm text-[10px]" data-testid="service-alerts-count">{alerts.length}</Badge>
            </div>
            <Button size="sm" variant="outline" data-testid="check-now-btn" onClick={checkNow} disabled={checking}
              className="rounded-sm uppercase tracking-[0.15em] text-[10px] h-7">
              <RefreshCw className={`w-3 h-3 mr-1 ${checking ? "animate-spin" : ""}`}/> {checking ? "Checking…" : "Check Now"}
            </Button>
          </div>
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.id} data-testid={`alert-row-${a.id}`}
                className="flex items-center gap-3 border border-border bg-secondary/40 rounded-sm px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.vehicle_label} <span className="text-muted-foreground font-normal">· {a.customer_name} · {a.customer_phone}</span></div>
                  <div className="text-xs text-muted-foreground">{a.message}</div>
                </div>
                <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider shrink-0" data-testid={`alert-trigger-${a.id}`}>
                  {a.trigger === "both" ? "Date + Odometer" : a.trigger === "odometer" ? "Odometer" : "Date"}
                </Badge>
                <Button size="sm" variant="ghost" data-testid={`alert-dismiss-${a.id}`} onClick={() => dismissAlert(a.id)}
                  className="rounded-sm uppercase tracking-[0.15em] text-[10px] h-7 shrink-0">
                  <X className="w-3 h-3 mr-1"/> Dismiss
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border bg-card rounded-sm p-3 mb-4 flex gap-3 flex-wrap items-end" data-testid="fu-filters">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Due From</Label>
          <Input data-testid="fu-filter-from" type="date" value={filters.from} onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Due To</Label>
          <Input data-testid="fu-filter-to" type="date" value={filters.to} onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Follow Up Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
            <SelectTrigger data-testid="fu-filter-status" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="fu-filter-status-all" value="all">All Status</SelectItem>
              <SelectItem data-testid="fu-filter-status-not_started" value="not_started">Not Started</SelectItem>
              <SelectItem data-testid="fu-filter-status-pending" value="pending">Pending</SelectItem>
              <SelectItem data-testid="fu-filter-status-completed" value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Job Type</Label>
          <Select value={filters.service} onValueChange={(v) => setFilters(f => ({ ...f, service: v }))}>
            <SelectTrigger data-testid="fu-filter-service" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem data-testid="fu-filter-service-all" value="all">All Job Types</SelectItem>
              {serviceTypes.map(s => <SelectItem key={s} data-testid={`fu-filter-service-${s}`} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Badge variant="outline" className="ml-auto rounded-sm" data-testid="fu-count">{filtered.length} of {rows.length} shown</Badge>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="fu-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-3 w-10">
                <Checkbox data-testid="fu-select-all" checked={someChecked ? "indeterminate" : allChecked} onCheckedChange={toggleAll}/>
              </th>
              <th className="text-left p-3">Customer Name</th>
              <th className="text-left p-3">Mobile</th>
              <th className="text-left p-3">Vehicle Number</th>
              <th className="text-left p-3">Last Service Date</th>
              <th className="text-left p-3">Next Due Date</th>
              <th className="text-left p-3">Due Odometer</th>
              <th className="text-left p-3">Job Type</th>
              <th className="text-left p-3">Follow Up Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(r => {
              const key = `${r.customer_id}:${r.vehicle_id}`;
              return (
                <tr key={key} data-testid={`fu-row-${key}`}
                  className={`border-t border-border ${r.overdue ? "bg-destructive/10 border-l-2 border-l-destructive" : ""}`}>
                  <td className="p-3">
                    <Checkbox data-testid={`fu-check-${key}`} checked={!!selected[key]} onCheckedChange={(v) => toggleOne(r, v)}/>
                  </td>
                  <td className="p-3 font-semibold">{r.customer_name}</td>
                  <td className="p-3 font-mono text-xs">{r.mobile}</td>
                  <td className="p-3 font-mono text-xs text-primary">{r.vehicle_number}</td>
                  <td className="p-3 text-xs">{fmtDate(r.last_service_date)}</td>
                  <td className={`p-3 text-xs ${r.overdue ? "text-destructive font-bold" : ""}`} data-testid={`fu-due-${key}`}>{fmtDate(r.next_due_date)}</td>
                  <td className="p-3 text-xs font-mono" data-testid={`fu-odo-${key}`}>{r.next_service_odometer ? `${Number(r.next_service_odometer).toLocaleString("en-IN")} km` : "—"}</td>
                  <td className="p-3 text-xs">{r.job_type || "—"}</td>
                  <td className="p-3" data-testid={`fu-status-badge-${key}`}>{badge(r.status)}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" data-testid={`fu-action-${key}`} onClick={() => openSingle(r)}
                      className="rounded-sm uppercase tracking-[0.15em] text-[10px] h-7">
                      <PhoneCall className="w-3 h-3 mr-1"/> Follow Up
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={10} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="fu-empty">
                {rows.length ? "No customers match the current filters" : "No completed job cards yet"}
              </td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <FollowUpDialog open={dialog.open} onClose={closeDialog} row={dialog.row}
        bulkCount={dialog.bulk ? selectedRows.length : 0} staff={staff}
        onSaved={() => { setSelected({}); load(); }}/>
    </div>
  );
}
